import { ChatTransport, isToolUIPart, UIMessage, UIMessageChunk } from "ai";

import { toAssistantErrorCode } from "./assistantErrorCode";

export interface AssistantTurnStart {
  aiSessionId: string;
  turnMessageId: string;
}

interface AssistantApprovalTurn {
  type: "approval";
  messageId: string;
  approvals: Array<{ id: string; approved: boolean; note?: string }>;
}

export type AssistantTurn<TMessageExtras extends object> =
  | ({ type: "message"; id: string; text: string } & TMessageExtras)
  | AssistantApprovalTurn;

export interface AssistantStreamHandlers {
  onData: (chunk: unknown) => void;
  onError: (error: unknown) => void;
  onComplete: () => void;
}

export interface AssistantChatProcedures<TStreamContext extends object, TMessageExtras extends object> {
  send: (input: {
    aiSessionId?: string;
    turn: AssistantTurn<TMessageExtras>;
  }) => Promise<{ data: AssistantTurnStart | null; error: { code: string; httpStatus?: number } | null }>;
  stream: (
    input: AssistantTurnStart & TStreamContext,
    handlers: AssistantStreamHandlers
  ) => { unsubscribe: () => void };
  getConversation: (input: { aiSessionId: string }) => Promise<{ canResume: boolean; turnMessageId: string | null }>;
  cancel: (input: AssistantTurnStart) => Promise<unknown>;
}

export interface AssistantChatTransportOptions<TStreamContext extends object, TMessageExtras extends object> {
  aiSessionId?: string;
  onSessionCreated?: (id: string) => void;
  getStreamContext: () => TStreamContext;
  getMessageTurnExtras?: (message: UIMessage) => TMessageExtras;
}

export interface AssistantChatTransport extends ChatTransport<UIMessage> {
  getAiSessionId: () => string | null;
  waitForTurnStart: () => Promise<string | null>;
  cancelTurn: (turnMessageId: string) => Promise<void>;
}

export const createTrpcChatTransport = <
  TStreamContext extends object,
  TMessageExtras extends object = Record<never, never>,
>(
  procedures: AssistantChatProcedures<TStreamContext, TMessageExtras>,
  options: AssistantChatTransportOptions<TStreamContext, TMessageExtras>
): AssistantChatTransport => {
  const state: { aiSessionId: string | null; turnStart: Promise<string | null> } = {
    aiSessionId: options.aiSessionId ?? null,
    turnStart: Promise.resolve(options.aiSessionId ?? null),
  };
  const getMessageTurnExtras =
    options.getMessageTurnExtras ?? ((() => ({})) as unknown as (message: UIMessage) => TMessageExtras);
  const subscribe = (turnStart: AssistantTurnStart, abortSignal?: AbortSignal) =>
    subscribeToTurn(
      (handlers) => procedures.stream({ ...turnStart, ...options.getStreamContext() }, handlers),
      abortSignal
    );

  return {
    getAiSessionId: () => state.aiSessionId,
    waitForTurnStart: () => state.turnStart,

    cancelTurn: async (turnMessageId) => {
      const aiSessionId = await state.turnStart;
      if (aiSessionId) {
        await procedures.cancel({ aiSessionId, turnMessageId });
      }
    },

    sendMessages: async ({ messages, abortSignal }) => {
      abortSignal?.throwIfAborted();
      const lastMessage = messages.at(-1);
      if (!lastMessage || lastMessage.role === "system") {
        throw new Error("invalid_messages");
      }
      const turn =
        lastMessage.role === "user"
          ? toMessageTurn(lastMessage, getMessageTurnExtras(lastMessage))
          : toApprovalTurn(lastMessage);
      const start = procedures.send({ aiSessionId: state.aiSessionId ?? undefined, turn });
      state.turnStart = start.then(
        (response) => response.data?.aiSessionId ?? state.aiSessionId,
        () => state.aiSessionId
      );
      const started = await start.catch((error: unknown) => {
        throw new Error(toAssistantErrorCode(error));
      });

      if (!started.data) {
        throw new Error(toAssistantErrorCode(started.error));
      }

      const { aiSessionId, turnMessageId } = started.data;
      state.aiSessionId = aiSessionId;
      options.onSessionCreated?.(aiSessionId);
      abortSignal?.throwIfAborted();
      return subscribe({ aiSessionId, turnMessageId }, abortSignal);
    },

    reconnectToStream: async ({ abortSignal }) => {
      if (!state.aiSessionId) {
        return null;
      }
      const aiSessionId = state.aiSessionId;
      const conversation = await procedures.getConversation({ aiSessionId }).catch((error: unknown) => {
        throw new Error(toAssistantErrorCode(error));
      });
      if (!conversation.canResume || !conversation.turnMessageId) {
        return null;
      }
      abortSignal?.throwIfAborted();
      return subscribe({ aiSessionId, turnMessageId: conversation.turnMessageId }, abortSignal);
    },
  };
};

const toMessageTurn = <TMessageExtras extends object>(
  message: UIMessage,
  extras: TMessageExtras
): AssistantTurn<TMessageExtras> => ({
  ...extras,
  type: "message" as const,
  id: message.id,
  text: message.parts
    .filter((part) => part.type === "text")
    .map((part) => part.text)
    .join("\n"),
});

const toApprovalTurn = (message: UIMessage): AssistantApprovalTurn => ({
  type: "approval",
  messageId: message.id,
  approvals: message.parts
    .filter(isToolUIPart)
    .filter((part) => part.state === "approval-responded")
    .map((part) => ({
      id: part.approval.id,
      approved: part.approval.approved,
      note: part.approval.approved ? part.approval.reason : undefined,
    })),
});

const subscribeToTurn = (
  start: (handlers: AssistantStreamHandlers) => { unsubscribe: () => void },
  abortSignal?: AbortSignal
): ReadableStream<UIMessageChunk> => {
  return new ReadableStream<UIMessageChunk>({
    start: (controller) => {
      const lifecycle = { isSettled: false };
      const settle = (finish: () => void) => {
        if (lifecycle.isSettled) {
          return;
        }
        lifecycle.isSettled = true;
        finish();
      };

      const subscription = start({
        onData: (chunk) => {
          if (!lifecycle.isSettled) {
            controller.enqueue(chunk as UIMessageChunk);
          }
        },
        onError: (error) => settle(() => controller.error(new Error(toAssistantErrorCode(error)))),
        onComplete: () => settle(() => controller.close()),
      });

      abortSignal?.addEventListener(
        "abort",
        () => {
          subscription.unsubscribe();
          settle(() => controller.close());
        },
        { once: true }
      );
    },
  });
};
